import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { resolveImageUrl } from "../lib/imageUrl";
import { 
  Diamond, 
  LayoutDashboard, 
  Users, 
  FileText, 
  MessageSquare,
  Globe,
  Package,
  Palette,
  TrendingUp,
  ShoppingCart,
  DollarSign,
  Search,
  Eye,
  Trash2,
  ChevronRight,
  RefreshCw,
  ArrowLeft,
  Plus,
  Edit,
  Truck,
  CheckCircle,
  Clock,
  LogOut,
  ShoppingBag,
  Mail,
  Phone
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [designRequests, setDesignRequests] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [leadCategory, setLeadCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: "", description: "", price: "",
    lab_grown_price: "", natural_price: "",
    category: "rings",
    gem_type: "", material: "22K Gold", image_url: "",
    carat: "", cut: "", color: "", clarity: "",
    weight: "", dimensions: "", certification: "",
    in_stock: true, featured: false
  });
  const [abandonedCarts, setAbandonedCarts] = useState([]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, leadsRes, designsRes, inquiriesRes, contactsRes, ordersRes, productsRes, abandonedRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard/stats`),
        axios.get(`${API}/leads`),
        axios.get(`${API}/design-requests`),
        axios.get(`${API}/inquiries`),
        axios.get(`${API}/contacts`),
        axios.get(`${API}/admin/orders`),
        axios.get(`${API}/products`),
        axios.get(`${API}/admin/abandoned-carts`)
      ]);
      setStats(statsRes.data);
      setLeads(leadsRes.data);
      setDesignRequests(designsRes.data);
      setInquiries(inquiriesRes.data);
      setContacts(contactsRes.data);
      setOrders(ordersRes.data);
      setProducts(productsRes.data);
      setAbandonedCarts(abandonedRes.data);
    } catch (err) {
      toast.error("Failed to load dashboard data");
      console.error("Bulk dashboard fetch failed:", err);
      // Try individual endpoints if bulk fails
      try {
        const statsRes = await axios.get(`${API}/dashboard/stats`);
        setStats(statsRes.data);
      } catch (e) {
        console.error("Fallback stats fetch failed:", e);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Check if admin is logged in
    const token = localStorage.getItem("vk_admin_token");
    if (!token) {
      navigate("/admin");
      return;
    }
    setIsAuthenticated(true);
    fetchAllData();
  }, [navigate, fetchAllData]);

  const handleLogout = () => {
    localStorage.removeItem("vk_admin_token");
    localStorage.removeItem("vk_admin_user");
    toast.success("Logged out successfully");
    navigate("/admin");
  };

  const updateLeadStatus = async (leadId, status) => {
    try {
      await axios.patch(`${API}/leads/${leadId}`, { status });
      toast.success("Lead status updated");
      fetchAllData();
    } catch (err) {
      toast.error("Failed to update lead");
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    try {
      await axios.delete(`${API}/leads/${leadId}`);
      toast.success("Lead deleted");
      fetchAllData();
    } catch (err) {
      toast.error("Failed to delete lead");
    }
  };

  const updateDesignStatus = async (requestId, status) => {
    try {
      await axios.patch(`${API}/design-requests/${requestId}?status=${status}`);
      toast.success("Design request updated");
      fetchAllData();
    } catch (err) {
      toast.error("Failed to update design request");
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.patch(`${API}/admin/orders/${orderId}?status=${status}`);
      toast.success("Order status updated");
      fetchAllData();
    } catch (err) {
      toast.error("Failed to update order");
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      const lg = productForm.lab_grown_price ? parseFloat(productForm.lab_grown_price) : null;
      const nat = productForm.natural_price ? parseFloat(productForm.natural_price) : null;
      const productData = {
        ...productForm,
        // `price` (display price) defaults to lab-grown if provided, else the raw price field
        price: lg ?? parseFloat(productForm.price || 0),
        lab_grown_price: lg,
        natural_price: nat,
        gem_variants: productForm.gem_type ? [`${productForm.gem_type} - Premium`, `${productForm.gem_type} - Standard`] : null
      };
      
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, { ...productData, id: editingProduct.id });
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/admin/products`, productData);
        toast.success("Product created");
      }
      setProductDialogOpen(false);
      setEditingProduct(null);
      setProductForm({
        name: "", description: "", price: "",
        lab_grown_price: "", natural_price: "",
        category: "rings",
        gem_type: "", material: "22K Gold", image_url: "",
        carat: "", cut: "", color: "", clarity: "",
        weight: "", dimensions: "", certification: "",
        in_stock: true, featured: false
      });
      fetchAllData();
    } catch (err) {
      toast.error("Failed to save product");
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`);
      toast.success("Product deleted");
      fetchAllData();
    } catch (err) {
      toast.error("Failed to delete product");
    }
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      price: (product.price ?? "").toString(),
      lab_grown_price: (product.lab_grown_price ?? "").toString(),
      natural_price: (product.natural_price ?? "").toString(),
      category: product.category,
      gem_type: product.gem_type || "",
      material: product.material,
      image_url: product.image_url,
      carat: product.carat || "",
      cut: product.cut || "",
      color: product.color || "",
      clarity: product.clarity || "",
      weight: product.weight || "",
      dimensions: product.dimensions || "",
      certification: product.certification || "",
      in_stock: product.in_stock,
      featured: product.featured
    });
    setProductDialogOpen(true);
  };

  const filteredLeads = leads.filter(lead => {
    const matchesCategory = leadCategory === "all" || lead.category === leadCategory;
    const matchesSearch = searchQuery === "" || 
      lead.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.company?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      new: "status-new",
      contacted: "status-contacted",
      qualified: "status-qualified",
      closed: "status-closed",
      pending: "status-new",
      confirmed: "status-new",
      in_progress: "status-contacted",
      shipped: "status-contacted",
      delivered: "status-qualified",
      completed: "status-qualified"
    };
    return statusClasses[status] || "status-new";
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#0F0F0F] border-r border-[#C9A962]/20 flex flex-col" data-testid="admin-sidebar">
        <div className="p-6 border-b border-[#C9A962]/20">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-logo">
            <Diamond className="w-7 h-7 text-[#C9A962]" />
            <span className="text-lg font-light text-[#C9A962]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              VK Gems CRM
            </span>
          </Link>
        </div>
        
        <nav className="flex-1 p-4 space-y-1">
          <button 
            onClick={() => setActiveTab("dashboard")}
            className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${activeTab === "dashboard" ? "bg-gradient-to-r from-[#C9A962] to-[#8B6914] text-[#0A0A0A]" : "text-[#8B7355] hover:bg-[#C9A962]/10"}`}
            data-testid="nav-dashboard"
          >
            <LayoutDashboard className="w-5 h-5" />
            Dashboard
          </button>
          <button 
            onClick={() => setActiveTab("orders")}
            className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${activeTab === "orders" ? "bg-gradient-to-r from-[#C9A962] to-[#8B6914] text-[#0A0A0A]" : "text-[#8B7355] hover:bg-[#C9A962]/10"}`}
            data-testid="nav-orders"
          >
            <ShoppingCart className="w-5 h-5" />
            Orders
          </button>
          <button 
            onClick={() => setActiveTab("products")}
            className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${activeTab === "products" ? "bg-gradient-to-r from-[#C9A962] to-[#8B6914] text-[#0A0A0A]" : "text-[#8B7355] hover:bg-[#C9A962]/10"}`}
            data-testid="nav-products"
          >
            <Package className="w-5 h-5" />
            Products
          </button>
          <button 
            onClick={() => setActiveTab("leads")}
            className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${activeTab === "leads" ? "bg-gradient-to-r from-[#C9A962] to-[#8B6914] text-[#0A0A0A]" : "text-[#8B7355] hover:bg-[#C9A962]/10"}`}
            data-testid="nav-leads"
          >
            <Users className="w-5 h-5" />
            Leads
          </button>
          <button 
            onClick={() => setActiveTab("designs")}
            className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${activeTab === "designs" ? "bg-gradient-to-r from-[#C9A962] to-[#8B6914] text-[#0A0A0A]" : "text-[#8B7355] hover:bg-[#C9A962]/10"}`}
            data-testid="nav-designs"
          >
            <Palette className="w-5 h-5" />
            Design Requests
          </button>
          <button 
            onClick={() => setActiveTab("inquiries")}
            className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${activeTab === "inquiries" ? "bg-gradient-to-r from-[#C9A962] to-[#8B6914] text-[#0A0A0A]" : "text-[#8B7355] hover:bg-[#C9A962]/10"}`}
            data-testid="nav-inquiries"
          >
            <Globe className="w-5 h-5" />
            Export Inquiries
          </button>
          <button 
            onClick={() => setActiveTab("contacts")}
            className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${activeTab === "contacts" ? "bg-gradient-to-r from-[#C9A962] to-[#8B6914] text-[#0A0A0A]" : "text-[#8B7355] hover:bg-[#C9A962]/10"}`}
            data-testid="nav-contacts"
          >
            <MessageSquare className="w-5 h-5" />
            Messages
          </button>
          <button 
            onClick={() => setActiveTab("abandoned")}
            className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${activeTab === "abandoned" ? "bg-gradient-to-r from-[#C9A962] to-[#8B6914] text-[#0A0A0A]" : "text-[#8B7355] hover:bg-[#C9A962]/10"}`}
            data-testid="nav-abandoned"
          >
            <ShoppingBag className="w-5 h-5" />
            Abandoned Carts
            {abandonedCarts.filter(c => c.status === "abandoned").length > 0 && (
              <span className="ml-auto px-2 py-0.5 bg-red-500/20 text-red-400 text-[10px] rounded">
                {abandonedCarts.filter(c => c.status === "abandoned").length}
              </span>
            )}
          </button>
        </nav>

        <div className="p-4 border-t border-[#C9A962]/20 space-y-2">
          <Link to="/" className="flex items-center gap-2 text-sm text-[#8B7355] hover:text-[#C9A962] transition-colors" data-testid="back-to-site">
            <ArrowLeft className="w-4 h-4" />
            Back to Website
          </Link>
          <button 
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-red-400 hover:text-red-300 transition-colors w-full"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Top Bar */}
        <header className="h-16 bg-[#0F0F0F] border-b border-[#C9A962]/20 flex items-center justify-between px-6" data-testid="admin-header">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="w-4 h-4 text-[#8B7355] absolute left-3 top-1/2 -translate-y-1/2" />
              <input 
                type="text" 
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-[#0A0A0A] border border-[#C9A962]/20 text-[#E8D5B5] pl-10 pr-4 py-2 text-sm focus:outline-none focus:border-[#C9A962] w-64"
                data-testid="search-input"
              />
            </div>
          </div>
          <button 
            onClick={fetchAllData}
            className="flex items-center gap-2 text-sm text-[#8B7355] hover:text-[#C9A962] transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </header>

        <div className="p-6">
          {/* Dashboard Tab */}
          {activeTab === "dashboard" && (
            <div data-testid="dashboard-content">
              <h1 className="text-3xl font-light text-[#E8D5B5] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Dashboard Overview
              </h1>

              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-[#0F0F0F] border border-[#C9A962]/20 p-6" data-testid="stat-total-revenue">
                  <div className="flex items-center justify-between mb-4">
                    <DollarSign className="w-8 h-8 text-[#C9A962]" />
                    <span className="text-xs text-green-500 uppercase tracking-wider">Revenue</span>
                  </div>
                  <p className="text-3xl font-light text-[#E8D5B5]">{formatPrice(stats?.total_revenue || 0)}</p>
                  <p className="text-sm text-[#8B7355]">Total Revenue</p>
                </div>

                <div className="bg-[#0F0F0F] border border-[#C9A962]/20 p-6" data-testid="stat-total-orders">
                  <div className="flex items-center justify-between mb-4">
                    <ShoppingCart className="w-8 h-8 text-[#C9A962]" />
                    <span className="text-xs text-blue-500 uppercase tracking-wider">Orders</span>
                  </div>
                  <p className="text-3xl font-light text-[#E8D5B5]">{stats?.total_orders || 0}</p>
                  <p className="text-sm text-[#8B7355]">Total Orders</p>
                </div>

                <div className="bg-[#0F0F0F] border border-[#C9A962]/20 p-6" data-testid="stat-total-leads">
                  <div className="flex items-center justify-between mb-4">
                    <Users className="w-8 h-8 text-[#C9A962]" />
                    <span className="text-xs text-[#C9A962] uppercase tracking-wider">CRM</span>
                  </div>
                  <p className="text-3xl font-light text-[#E8D5B5]">{stats?.total_leads || 0}</p>
                  <p className="text-sm text-[#8B7355]">Total Leads</p>
                </div>

                <div className="bg-[#0F0F0F] border border-[#C9A962]/20 p-6" data-testid="stat-total-products">
                  <div className="flex items-center justify-between mb-4">
                    <Package className="w-8 h-8 text-[#C9A962]" />
                    <span className="text-xs text-purple-500 uppercase tracking-wider">Inventory</span>
                  </div>
                  <p className="text-3xl font-light text-[#E8D5B5]">{stats?.total_products || 0}</p>
                  <p className="text-sm text-[#8B7355]">Products</p>
                </div>
              </div>

              {/* Secondary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div className="bg-[#0F0F0F] border border-[#C9A962]/20 p-4">
                  <p className="text-xs text-[#8B7355] uppercase tracking-wider mb-1">Pending Orders</p>
                  <p className="text-2xl font-light text-[#C9A962]">{stats?.pending_orders || 0}</p>
                </div>
                <div className="bg-[#0F0F0F] border border-[#C9A962]/20 p-4">
                  <p className="text-xs text-[#8B7355] uppercase tracking-wider mb-1">Shipped</p>
                  <p className="text-2xl font-light text-blue-400">{stats?.shipped_orders || 0}</p>
                </div>
                <div className="bg-[#0F0F0F] border border-[#C9A962]/20 p-4">
                  <p className="text-xs text-[#8B7355] uppercase tracking-wider mb-1">Delivered</p>
                  <p className="text-2xl font-light text-green-400">{stats?.delivered_orders || 0}</p>
                </div>
                <div className="bg-[#0F0F0F] border border-[#C9A962]/20 p-4">
                  <p className="text-xs text-[#8B7355] uppercase tracking-wider mb-1">Today's Orders</p>
                  <p className="text-2xl font-light text-[#E8D5B5]">{stats?.orders_today || 0}</p>
                </div>
              </div>

              {/* Recent Orders */}
              <div className="bg-[#0F0F0F] border border-[#C9A962]/20">
                <div className="p-6 border-b border-[#C9A962]/10 flex items-center justify-between">
                  <h2 className="text-xl font-light text-[#E8D5B5]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Recent Orders</h2>
                  <button onClick={() => setActiveTab("orders")} className="text-sm text-[#C9A962] flex items-center gap-1 hover:underline" data-testid="view-all-orders">
                    View All <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="data-table" data-testid="recent-orders-table">
                    <thead>
                      <tr>
                        <th>Order #</th>
                        <th>Customer</th>
                        <th>Items</th>
                        <th>Total</th>
                        <th>Status</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.slice(0, 5).map((order) => (
                        <tr key={order.id}>
                          <td className="font-mono text-[#C9A962]">{order.order_number}</td>
                          <td>{order.shipping_address?.first_name} {order.shipping_address?.last_name}</td>
                          <td>{order.items?.length} item(s)</td>
                          <td className="text-[#C9A962]">{formatPrice(order.total)}</td>
                          <td>
                            <span className={`status-badge ${getStatusBadge(order.status)}`}>
                              {order.status}
                            </span>
                          </td>
                          <td className="text-[#8B7355]">{formatDate(order.created_at)}</td>
                        </tr>
                      ))}
                      {orders.length === 0 && (
                        <tr>
                          <td colSpan={6} className="text-center text-[#8B7355] py-8">No orders yet</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === "orders" && (
            <div data-testid="orders-content">
              <h1 className="text-3xl font-light text-[#E8D5B5] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Order Management
              </h1>

              <div className="bg-[#0F0F0F] border border-[#C9A962]/20 overflow-x-auto">
                <table className="data-table" data-testid="orders-table">
                  <thead>
                    <tr>
                      <th>Order #</th>
                      <th>Customer</th>
                      <th>Email</th>
                      <th>Items</th>
                      <th>Total</th>
                      <th>Delivery</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.id} data-testid={`order-row-${order.id}`}>
                        <td className="font-mono text-[#C9A962]">{order.order_number}</td>
                        <td>{order.shipping_address?.first_name} {order.shipping_address?.last_name}</td>
                        <td>{order.shipping_address?.email}</td>
                        <td>{order.items?.length} item(s)</td>
                        <td className="text-[#C9A962] font-medium">{formatPrice(order.total)}</td>
                        <td className="capitalize">{order.delivery_method}</td>
                        <td>
                          <select 
                            value={order.status} 
                            onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                            className="bg-[#0A0A0A] border border-[#C9A962]/20 text-[#E8D5B5] px-2 py-1 text-sm focus:outline-none focus:border-[#C9A962]"
                            data-testid={`order-status-${order.id}`}
                          >
                            <option value="confirmed">Confirmed</option>
                            <option value="processing">Processing</option>
                            <option value="shipped">Shipped</option>
                            <option value="delivered">Delivered</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </td>
                        <td className="text-[#8B7355]">{formatDate(order.created_at)}</td>
                        <td>
                          <button 
                            onClick={() => setSelectedOrder(order)}
                            className="p-2 hover:bg-[#C9A962]/10 transition-colors"
                            data-testid={`view-order-${order.id}`}
                          >
                            <Eye className="w-4 h-4 text-[#8B7355]" />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {orders.length === 0 && (
                      <tr>
                        <td colSpan={9} className="text-center text-[#8B7355] py-8">No orders yet</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Order Detail Modal */}
              <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
                <DialogContent className="bg-[#0F0F0F] border-[#C9A962]/20 text-[#E8D5B5] max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-xl font-light text-[#C9A962]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      Order Details
                    </DialogTitle>
                    <DialogDescription className="text-[#8B7355]">
                      {selectedOrder?.order_number}
                    </DialogDescription>
                  </DialogHeader>
                  {selectedOrder && (
                    <div className="space-y-6 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs text-[#8B7355] uppercase tracking-wider">Customer</label>
                          <p>{selectedOrder.shipping_address?.first_name} {selectedOrder.shipping_address?.last_name}</p>
                        </div>
                        <div>
                          <label className="text-xs text-[#8B7355] uppercase tracking-wider">Email</label>
                          <p>{selectedOrder.shipping_address?.email}</p>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-[#8B7355] uppercase tracking-wider">Shipping Address</label>
                        <p>{selectedOrder.shipping_address?.address_line1}</p>
                        <p>{selectedOrder.shipping_address?.city}, {selectedOrder.shipping_address?.state} {selectedOrder.shipping_address?.postal_code}</p>
                        <p>{selectedOrder.shipping_address?.country}</p>
                      </div>
                      <div>
                        <label className="text-xs text-[#8B7355] uppercase tracking-wider">Items</label>
                        <div className="space-y-2 mt-2">
                          {selectedOrder.items?.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-4 p-3 bg-[#0A0A0A] border border-[#C9A962]/10">
                              <img src={resolveImageUrl(item.product_image_url)} alt={item.title} className="w-12 h-12 object-cover" />
                              <div className="flex-1">
                                <p className="text-sm">{item.title}</p>
                                {item.selected_gem_variant && <p className="text-xs text-[#C9A962]">{item.selected_gem_variant}</p>}
                              </div>
                              <p className="text-sm">x{item.quantity}</p>
                              <p className="text-sm text-[#C9A962]">{formatPrice(item.subtotal)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="border-t border-[#C9A962]/20 pt-4 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-[#8B7355]">Subtotal</span>
                          <span>{formatPrice(selectedOrder.subtotal)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-[#8B7355]">Tax</span>
                          <span>{formatPrice(selectedOrder.tax)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-[#8B7355]">Shipping</span>
                          <span>{formatPrice(selectedOrder.shipping_cost)}</span>
                        </div>
                        <div className="flex justify-between text-lg font-medium pt-2 border-t border-[#C9A962]/20">
                          <span>Total</span>
                          <span className="text-[#C9A962]">{formatPrice(selectedOrder.total)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </div>
          )}

          {/* Products Tab */}
          {activeTab === "products" && (
            <div data-testid="products-content">
              <div className="flex items-center justify-between mb-8">
                <h1 className="text-3xl font-light text-[#E8D5B5]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Product Management
                </h1>
                <button 
                  onClick={() => { setEditingProduct(null); setProductForm({ name: "", description: "", price: "", lab_grown_price: "", natural_price: "", category: "rings", gem_type: "", material: "22K Gold", image_url: "", carat: "", cut: "", color: "", clarity: "", weight: "", dimensions: "", certification: "", in_stock: true, featured: false }); setProductDialogOpen(true); }}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#C9A962] to-[#8B6914] text-[#0A0A0A] font-medium hover:from-[#D4B872] hover:to-[#9A7A24]"
                  data-testid="add-product-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add Product
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((product) => (
                  <div key={product.id} className="bg-[#0F0F0F] border border-[#C9A962]/20 overflow-hidden" data-testid={`product-card-${product.id}`}>
                    <div className="aspect-square relative">
                      <img src={resolveImageUrl(product.image_url)} alt={product.name} className="w-full h-full object-cover" />
                      {product.featured && (
                        <span className="absolute top-2 left-2 px-2 py-1 bg-[#C9A962] text-[#0A0A0A] text-xs font-medium">Featured</span>
                      )}
                      {!product.in_stock && (
                        <span className="absolute top-2 right-2 px-2 py-1 bg-red-500 text-white text-xs font-medium">Out of Stock</span>
                      )}
                    </div>
                    <div className="p-4">
                      <p className="text-xs text-[#C9A962] uppercase tracking-wider">{product.category}</p>
                      <h3 className="text-lg text-[#E8D5B5] mt-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{product.name}</h3>
                      <p className="text-sm text-[#8B7355] mt-1 line-clamp-2">{product.description}</p>
                      <div className="flex items-center justify-between mt-4">
                        <span className="text-xl text-[#C9A962]">{formatPrice(product.price)}</span>
                        <div className="flex gap-2">
                          <button 
                            onClick={() => openEditProduct(product)}
                            className="p-2 border border-[#C9A962]/30 hover:bg-[#C9A962]/10"
                            data-testid={`edit-product-${product.id}`}
                          >
                            <Edit className="w-4 h-4 text-[#C9A962]" />
                          </button>
                          <button 
                            onClick={() => deleteProduct(product.id)}
                            className="p-2 border border-red-500/30 hover:bg-red-500/10"
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {products.length === 0 && (
                <div className="text-center py-20 bg-[#0F0F0F] border border-[#C9A962]/20">
                  <Package className="w-16 h-16 text-[#8B7355] mx-auto mb-4" />
                  <p className="text-[#8B7355]">No products yet. Add your first product!</p>
                </div>
              )}

              {/* Product Form Dialog */}
              <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
                <DialogContent className="bg-[#0F0F0F] border-[#C9A962]/20 text-[#E8D5B5] max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-xl font-light text-[#C9A962]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      {editingProduct ? "Edit Product" : "Add New Product"}
                    </DialogTitle>
                    <DialogDescription className="text-[#8B7355]">
                      {editingProduct ? "Update product details" : "Add a new product to your catalog"}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleProductSubmit} className="space-y-4 mt-4">
                    <div>
                      <label className="text-xs text-[#8B7355] uppercase tracking-wider">Product Name *</label>
                      <input type="text" required value={productForm.name} onChange={(e) => setProductForm({...productForm, name: e.target.value})} className="w-full bg-transparent border-b border-[#C9A962]/30 text-[#E8D5B5] py-2 focus:outline-none focus:border-[#C9A962]" data-testid="form-product-name" />
                    </div>
                    <div>
                      <label className="text-xs text-[#8B7355] uppercase tracking-wider">Description *</label>
                      <textarea required rows={3} value={productForm.description} onChange={(e) => setProductForm({...productForm, description: e.target.value})} className="w-full bg-transparent border-b border-[#C9A962]/30 text-[#E8D5B5] py-2 focus:outline-none focus:border-[#C9A962] resize-none" data-testid="form-product-description" />
                    </div>

                    {/* Pricing — Lab-Grown & Natural */}
                    <div className="border border-[#C9A962]/15 p-4">
                      <p className="text-xs text-[#C9A962] uppercase tracking-wider mb-3">Pricing</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs text-[#8B7355] uppercase tracking-wider">Lab-Grown Diamond Price (USD) *</label>
                          <input type="number" required min="0" step="0.01" value={productForm.lab_grown_price} onChange={(e) => setProductForm({...productForm, lab_grown_price: e.target.value, price: e.target.value})} placeholder="e.g. 12500" className="w-full bg-transparent border-b border-[#C9A962]/30 text-[#E8D5B5] py-2 focus:outline-none focus:border-[#C9A962]" data-testid="form-lab-grown-price" />
                        </div>
                        <div>
                          <label className="text-xs text-[#8B7355] uppercase tracking-wider">Natural Diamond Price (USD)</label>
                          <input type="number" min="0" step="0.01" value={productForm.natural_price} onChange={(e) => setProductForm({...productForm, natural_price: e.target.value})} placeholder="Leave empty if no upgrade" className="w-full bg-transparent border-b border-[#C9A962]/30 text-[#E8D5B5] py-2 focus:outline-none focus:border-[#C9A962]" data-testid="form-natural-price" />
                        </div>
                      </div>
                      <p className="text-[10px] text-[#8B7355] mt-2">Leave Natural empty for plain-gold pieces without diamonds.</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-[#8B7355] uppercase tracking-wider">Category *</label>
                        <select value={productForm.category} onChange={(e) => setProductForm({...productForm, category: e.target.value})} className="w-full bg-[#0F0F0F] border-b border-[#C9A962]/30 text-[#E8D5B5] py-2 focus:outline-none focus:border-[#C9A962]" data-testid="form-category">
                          <option value="rings">Rings</option>
                          <option value="necklaces">Necklaces</option>
                          <option value="earrings">Earrings</option>
                          <option value="bracelets">Bracelets</option>
                          <option value="watches">Watches</option>
                          <option value="grillz">Grillz</option>
                          <option value="chains">Chains</option>
                          <option value="anklets">Anklets</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-[#8B7355] uppercase tracking-wider">Material</label>
                        <select value={productForm.material} onChange={(e) => setProductForm({...productForm, material: e.target.value})} className="w-full bg-[#0F0F0F] border-b border-[#C9A962]/30 text-[#E8D5B5] py-2 focus:outline-none focus:border-[#C9A962]" data-testid="form-material">
                          <option value="22K Gold">22K Gold</option>
                          <option value="18K Gold">18K Gold</option>
                          <option value="14K Gold">14K Gold</option>
                          <option value="Platinum">Platinum</option>
                          <option value="Silver">Silver</option>
                          <option value="Stainless Steel">Stainless Steel</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="text-xs text-[#8B7355] uppercase tracking-wider">Gem Type</label>
                      <input type="text" value={productForm.gem_type} onChange={(e) => setProductForm({...productForm, gem_type: e.target.value})} placeholder="e.g., Emerald, Ruby, Diamond" className="w-full bg-transparent border-b border-[#C9A962]/30 text-[#E8D5B5] py-2 focus:outline-none focus:border-[#C9A962]" data-testid="form-gem-type" />
                    </div>

                    {/* Diamond 4 Cs */}
                    <div className="border border-[#C9A962]/15 p-4">
                      <p className="text-xs text-[#C9A962] uppercase tracking-wider mb-3">Diamond 4 Cs</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs text-[#8B7355] uppercase tracking-wider">Carat</label>
                          <input type="text" value={productForm.carat} onChange={(e) => setProductForm({...productForm, carat: e.target.value})} placeholder="e.g., 3.0 ct" className="w-full bg-transparent border-b border-[#C9A962]/30 text-[#E8D5B5] py-2 focus:outline-none focus:border-[#C9A962]" data-testid="form-carat" />
                        </div>
                        <div>
                          <label className="text-xs text-[#8B7355] uppercase tracking-wider">Cut</label>
                          <input type="text" value={productForm.cut} onChange={(e) => setProductForm({...productForm, cut: e.target.value})} placeholder="e.g., Excellent, Round Brilliant" className="w-full bg-transparent border-b border-[#C9A962]/30 text-[#E8D5B5] py-2 focus:outline-none focus:border-[#C9A962]" data-testid="form-cut" />
                        </div>
                        <div>
                          <label className="text-xs text-[#8B7355] uppercase tracking-wider">Color</label>
                          <input type="text" value={productForm.color} onChange={(e) => setProductForm({...productForm, color: e.target.value})} placeholder="e.g., D, E, F" className="w-full bg-transparent border-b border-[#C9A962]/30 text-[#E8D5B5] py-2 focus:outline-none focus:border-[#C9A962]" data-testid="form-color" />
                        </div>
                        <div>
                          <label className="text-xs text-[#8B7355] uppercase tracking-wider">Clarity</label>
                          <input type="text" value={productForm.clarity} onChange={(e) => setProductForm({...productForm, clarity: e.target.value})} placeholder="e.g., VVS1, VS2" className="w-full bg-transparent border-b border-[#C9A962]/30 text-[#E8D5B5] py-2 focus:outline-none focus:border-[#C9A962]" data-testid="form-clarity" />
                        </div>
                      </div>
                    </div>

                    {/* Other specs */}
                    <div className="border border-[#C9A962]/15 p-4">
                      <p className="text-xs text-[#C9A962] uppercase tracking-wider mb-3">Product Specs</p>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="text-xs text-[#8B7355] uppercase tracking-wider">Weight</label>
                          <input type="text" value={productForm.weight} onChange={(e) => setProductForm({...productForm, weight: e.target.value})} placeholder="e.g., 5.2 g" className="w-full bg-transparent border-b border-[#C9A962]/30 text-[#E8D5B5] py-2 focus:outline-none focus:border-[#C9A962]" data-testid="form-weight" />
                        </div>
                        <div>
                          <label className="text-xs text-[#8B7355] uppercase tracking-wider">Dimensions</label>
                          <input type="text" value={productForm.dimensions} onChange={(e) => setProductForm({...productForm, dimensions: e.target.value})} placeholder="e.g., 20 x 15 mm" className="w-full bg-transparent border-b border-[#C9A962]/30 text-[#E8D5B5] py-2 focus:outline-none focus:border-[#C9A962]" data-testid="form-dimensions" />
                        </div>
                        <div>
                          <label className="text-xs text-[#8B7355] uppercase tracking-wider">Certification</label>
                          <input type="text" value={productForm.certification} onChange={(e) => setProductForm({...productForm, certification: e.target.value})} placeholder="e.g., GIA, IGI" className="w-full bg-transparent border-b border-[#C9A962]/30 text-[#E8D5B5] py-2 focus:outline-none focus:border-[#C9A962]" data-testid="form-certification" />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="text-xs text-[#8B7355] uppercase tracking-wider">Image URL or Path *</label>
                      <input type="text" required value={productForm.image_url} onChange={(e) => setProductForm({...productForm, image_url: e.target.value})} placeholder="/api/static/products/xxx.png or https://..." className="w-full bg-transparent border-b border-[#C9A962]/30 text-[#E8D5B5] py-2 focus:outline-none focus:border-[#C9A962]" data-testid="form-image-url" />
                      <p className="text-[10px] text-[#8B7355] mt-1">Accepts relative paths (/api/static/products/...) or full URLs.</p>
                    </div>
                    <div className="flex gap-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={productForm.in_stock} onChange={(e) => setProductForm({...productForm, in_stock: e.target.checked})} className="accent-[#C9A962]" />
                        <span className="text-sm text-[#8B7355]">In Stock</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={productForm.featured} onChange={(e) => setProductForm({...productForm, featured: e.target.checked})} className="accent-[#C9A962]" />
                        <span className="text-sm text-[#8B7355]">Featured</span>
                      </label>
                    </div>
                    <button type="submit" className="w-full py-3 bg-gradient-to-r from-[#C9A962] to-[#8B6914] text-[#0A0A0A] font-semibold" data-testid="form-submit-btn">
                      {editingProduct ? "Update Product" : "Add Product"}
                    </button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          )}

          {/* Leads Tab */}
          {activeTab === "leads" && (
            <div data-testid="leads-content">
              <div className="flex items-center justify-between mb-8">
                <h1 className="text-3xl font-light text-[#E8D5B5]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Lead Management
                </h1>
              </div>

              <Tabs value={leadCategory} onValueChange={setLeadCategory} className="mb-6">
                <TabsList className="bg-transparent border border-[#C9A962]/20 p-1" data-testid="lead-category-tabs">
                  <TabsTrigger value="all" className="crm-tab data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#C9A962] data-[state=active]:to-[#8B6914] data-[state=active]:text-[#0A0A0A]" data-testid="tab-all-leads">
                    All ({leads.length})
                  </TabsTrigger>
                  <TabsTrigger value="international_export" className="crm-tab data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#C9A962] data-[state=active]:to-[#8B6914] data-[state=active]:text-[#0A0A0A]" data-testid="tab-international">
                    International
                  </TabsTrigger>
                  <TabsTrigger value="domestic_wholesale" className="crm-tab data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#C9A962] data-[state=active]:to-[#8B6914] data-[state=active]:text-[#0A0A0A]" data-testid="tab-domestic">
                    Domestic
                  </TabsTrigger>
                  <TabsTrigger value="custom_jewelry" className="crm-tab data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#C9A962] data-[state=active]:to-[#8B6914] data-[state=active]:text-[#0A0A0A]" data-testid="tab-custom">
                    Custom
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              <div className="bg-[#0F0F0F] border border-[#C9A962]/20 overflow-x-auto">
                <table className="data-table" data-testid="leads-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Contact</th>
                      <th>Company</th>
                      <th>Category</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLeads.map((lead) => (
                      <tr key={lead.id} data-testid={`lead-row-${lead.id}`}>
                        <td className="font-medium">{lead.name}</td>
                        <td>
                          <div>
                            <p>{lead.email}</p>
                            <p className="text-xs text-[#8B7355]">{lead.phone}</p>
                          </div>
                        </td>
                        <td>{lead.company || "-"}</td>
                        <td>
                          <span className="text-xs uppercase tracking-wider text-[#C9A962]">
                            {lead.category.replace("_", " ")}
                          </span>
                        </td>
                        <td>
                          <select 
                            value={lead.status} 
                            onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                            className="bg-[#0A0A0A] border border-[#C9A962]/20 text-[#E8D5B5] px-2 py-1 text-sm focus:outline-none focus:border-[#C9A962]"
                            data-testid={`status-select-${lead.id}`}
                          >
                            <option value="new">New</option>
                            <option value="contacted">Contacted</option>
                            <option value="qualified">Qualified</option>
                            <option value="closed">Closed</option>
                          </select>
                        </td>
                        <td className="text-[#8B7355]">{formatDate(lead.created_at)}</td>
                        <td>
                          <div className="flex items-center gap-2">
                            <button 
                              onClick={() => setSelectedLead(lead)}
                              className="p-2 hover:bg-[#C9A962]/10 transition-colors"
                              data-testid={`view-lead-${lead.id}`}
                            >
                              <Eye className="w-4 h-4 text-[#8B7355]" />
                            </button>
                            <button 
                              onClick={() => deleteLead(lead.id)}
                              className="p-2 hover:bg-red-500/10 transition-colors"
                              data-testid={`delete-lead-${lead.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredLeads.length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-center text-[#8B7355] py-8">No leads found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Lead Detail Modal */}
              <Dialog open={!!selectedLead} onOpenChange={() => setSelectedLead(null)}>
                <DialogContent className="bg-[#0F0F0F] border-[#C9A962]/20 text-[#E8D5B5] max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="text-xl font-light text-[#C9A962]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Lead Details</DialogTitle>
                    <DialogDescription className="text-[#8B7355]">Complete lead information</DialogDescription>
                  </DialogHeader>
                  {selectedLead && (
                    <div className="space-y-4 mt-4">
                      <div>
                        <label className="text-xs text-[#8B7355] uppercase tracking-wider">Name</label>
                        <p>{selectedLead.name}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs text-[#8B7355] uppercase tracking-wider">Email</label>
                          <p>{selectedLead.email}</p>
                        </div>
                        <div>
                          <label className="text-xs text-[#8B7355] uppercase tracking-wider">Phone</label>
                          <p>{selectedLead.phone}</p>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-[#8B7355] uppercase tracking-wider">Company</label>
                        <p>{selectedLead.company || "N/A"}</p>
                      </div>
                      <div>
                        <label className="text-xs text-[#8B7355] uppercase tracking-wider">Message</label>
                        <p className="whitespace-pre-wrap">{selectedLead.message}</p>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </div>
          )}

          {/* Design Requests Tab */}
          {activeTab === "designs" && (
            <div data-testid="designs-content">
              <h1 className="text-3xl font-light text-[#E8D5B5] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                CAD/CAM Design Requests
              </h1>

              <div className="bg-[#0F0F0F] border border-[#C9A962]/20 overflow-x-auto">
                <table className="data-table" data-testid="designs-table">
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>Contact</th>
                      <th>Description</th>
                      <th>Material</th>
                      <th>Budget</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {designRequests.map((request) => (
                      <tr key={request.id} data-testid={`design-row-${request.id}`}>
                        <td className="font-medium">{request.client_name}</td>
                        <td>
                          <div>
                            <p>{request.client_email}</p>
                            <p className="text-xs text-[#8B7355]">{request.client_phone}</p>
                          </div>
                        </td>
                        <td className="max-w-xs truncate">{request.design_description}</td>
                        <td>{request.material_preference?.replace("_", " ") || "-"}</td>
                        <td>{request.budget_range?.replace("_", " ") || "-"}</td>
                        <td>
                          <select 
                            value={request.status} 
                            onChange={(e) => updateDesignStatus(request.id, e.target.value)}
                            className="bg-[#0A0A0A] border border-[#C9A962]/20 text-[#E8D5B5] px-2 py-1 text-sm focus:outline-none focus:border-[#C9A962]"
                          >
                            <option value="pending">Pending</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                          </select>
                        </td>
                        <td className="text-[#8B7355]">{formatDate(request.created_at)}</td>
                      </tr>
                    ))}
                    {designRequests.length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-center text-[#8B7355] py-8">No design requests yet</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Inquiries Tab */}
          {activeTab === "inquiries" && (
            <div data-testid="inquiries-content">
              <h1 className="text-3xl font-light text-[#E8D5B5] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Global Export Inquiries
              </h1>

              <div className="bg-[#0F0F0F] border border-[#C9A962]/20 overflow-x-auto">
                <table className="data-table" data-testid="inquiries-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Company</th>
                      <th>Country</th>
                      <th>Type</th>
                      <th>Product Interest</th>
                      <th>Assigned Office</th>
                      <th>Status</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inquiries.map((inquiry) => (
                      <tr key={inquiry.id} data-testid={`inquiry-row-${inquiry.id}`}>
                        <td className="font-medium">{inquiry.name}</td>
                        <td>{inquiry.company || "-"}</td>
                        <td>{inquiry.country}</td>
                        <td>
                          <span className="text-xs uppercase tracking-wider text-[#C9A962]">
                            {inquiry.inquiry_type}
                          </span>
                        </td>
                        <td className="max-w-xs truncate">{inquiry.product_interest}</td>
                        <td>
                          <span className={`text-xs uppercase tracking-wider ${inquiry.assigned_office === "gurgaon" ? "text-blue-400" : "text-green-400"}`}>
                            {inquiry.assigned_office}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge ${getStatusBadge(inquiry.status)}`}>
                            {inquiry.status}
                          </span>
                        </td>
                        <td className="text-[#8B7355]">{formatDate(inquiry.created_at)}</td>
                      </tr>
                    ))}
                    {inquiries.length === 0 && (
                      <tr>
                        <td colSpan={8} className="text-center text-[#8B7355] py-8">No inquiries yet</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Contacts Tab */}
          {activeTab === "contacts" && (
            <div data-testid="contacts-content">
              <h1 className="text-3xl font-light text-[#E8D5B5] mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Contact Messages
              </h1>

              <div className="grid gap-4">
                {contacts.map((contact) => (
                  <div key={contact.id} className="bg-[#0F0F0F] border border-[#C9A962]/20 p-6" data-testid={`contact-card-${contact.id}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-medium text-[#E8D5B5]">{contact.name}</h3>
                        <p className="text-sm text-[#8B7355]">{contact.email} • {contact.phone || "No phone"}</p>
                      </div>
                      <span className="text-xs text-[#8B7355]">{formatDate(contact.created_at)}</span>
                    </div>
                    <p className="text-sm text-[#C9A962] mb-2">{contact.subject}</p>
                    <p className="text-[#B8A88A]">{contact.message}</p>
                  </div>
                ))}
                {contacts.length === 0 && (
                  <div className="bg-[#0F0F0F] border border-[#C9A962]/20 p-12 text-center">
                    <MessageSquare className="w-12 h-12 text-[#8B7355] mx-auto mb-4" />
                    <p className="text-[#8B7355]">No contact messages yet</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Abandoned Carts Tab */}
          {activeTab === "abandoned" && (
            <div data-testid="abandoned-content">
              <div className="flex items-end justify-between mb-8">
                <div>
                  <h1 className="text-3xl font-light text-[#E8D5B5]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    Abandoned Carts
                  </h1>
                  <p className="text-sm text-[#8B7355] mt-2">
                    Customers who started checkout but didn't complete payment. Reach out to recover the sale.
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-light text-[#C9A962]" style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="abandoned-total-value">
                    ${abandonedCarts.filter(c => c.status === "abandoned").reduce((s, c) => s + (c.subtotal || 0), 0).toLocaleString()}
                  </p>
                  <p className="text-[10px] text-[#8B7355] uppercase tracking-wider">Recoverable value</p>
                </div>
              </div>

              <div className="grid gap-4">
                {abandonedCarts.length === 0 && (
                  <div className="bg-[#0F0F0F] border border-[#C9A962]/20 p-12 text-center" data-testid="abandoned-empty">
                    <ShoppingBag className="w-12 h-12 text-[#8B7355] mx-auto mb-4" />
                    <p className="text-[#8B7355]">No abandoned carts yet. Once a customer enters their email on checkout but leaves without paying, it'll appear here.</p>
                  </div>
                )}

                {abandonedCarts.map((c) => {
                  const isAbandoned = c.status === "abandoned";
                  const isContacted = c.status === "contacted";
                  const isConverted = c.status === "converted";
                  const fullName = [c.first_name, c.last_name].filter(Boolean).join(" ") || "(no name)";
                  return (
                    <div key={c.id} className="bg-[#0F0F0F] border border-[#C9A962]/20 p-6" data-testid={`abandoned-card-${c.id}`}>
                      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                        <div>
                          <h3 className="text-lg font-medium text-[#E8D5B5]">{fullName}</h3>
                          <div className="flex flex-wrap gap-3 mt-1 text-xs text-[#8B7355]">
                            <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</span>
                            {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</span>}
                            {c.country && <span>{c.country}</span>}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl text-[#C9A962]" data-testid={`abandoned-subtotal-${c.id}`}>${(c.subtotal || 0).toLocaleString()}</p>
                          <p className="text-[10px] text-[#8B7355] uppercase tracking-wider">Cart value</p>
                        </div>
                      </div>

                      {/* Items */}
                      {c.items?.length > 0 && (
                        <div className="bg-[#0A0A0A] border border-[#C9A962]/10 p-3 mb-3 space-y-2">
                          {c.items.map((item, idx) => (
                            <div key={`${c.id}-item-${idx}`} className="flex items-center gap-3 text-xs">
                              <img src={resolveImageUrl(item.product_image_url)} alt={item.title} className="w-10 h-10 object-cover" />
                              <div className="flex-1">
                                <p className="text-[#E8D5B5]">{item.title}</p>
                                <p className="text-[#8B7355]">Qty {item.quantity} · ${item.price?.toLocaleString()}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-2 text-xs text-[#8B7355]">
                          <Clock className="w-3 h-3" />
                          Updated {formatDate(c.updated_at || c.created_at)}
                          <span className={`ml-3 px-2 py-0.5 text-[10px] uppercase tracking-wider ${
                            isConverted ? "bg-green-500/15 text-green-400" :
                            isContacted ? "bg-blue-500/15 text-blue-400" :
                            "bg-red-500/15 text-red-400"
                          }`}>
                            {c.status}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <a
                            href={`mailto:${c.email}?subject=Your VK Gems cart is waiting&body=Hi ${c.first_name || ""},%0A%0AWe noticed you left some beautiful pieces in your cart.`}
                            className="px-3 py-1.5 text-xs border border-[#C9A962]/40 text-[#C9A962] hover:bg-[#C9A962]/10 transition-colors"
                            data-testid={`abandoned-email-${c.id}`}
                          >
                            Email
                          </a>
                          {!isContacted && !isConverted && (
                            <button
                              onClick={async () => {
                                try {
                                  await axios.patch(`${API}/admin/abandoned-carts/${c.id}?status=contacted`);
                                  toast.success("Marked as contacted");
                                  fetchAllData();
                                } catch {
                                  toast.error("Failed to update");
                                }
                              }}
                              className="px-3 py-1.5 text-xs bg-[#C9A962] text-[#0A0A0A] hover:opacity-90 transition-opacity"
                              data-testid={`abandoned-mark-contacted-${c.id}`}
                            >
                              Mark Contacted
                            </button>
                          )}
                          {isAbandoned && (
                            <button
                              onClick={async () => {
                                try {
                                  await axios.patch(`${API}/admin/abandoned-carts/${c.id}?status=converted`);
                                  toast.success("Marked as converted");
                                  fetchAllData();
                                } catch {
                                  toast.error("Failed to update");
                                }
                              }}
                              className="px-3 py-1.5 text-xs bg-green-600/80 text-white hover:bg-green-600 transition-colors"
                              data-testid={`abandoned-mark-converted-${c.id}`}
                            >
                              Converted
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
